// Step 6 — Proposed column / tactics (CAXeng.md §4, FUN_10008ca4 / FUN_100071b0 / FUN_10007619 / FUN_100089cb).
import { otherParty, TACTIC_DESCRIPTION, type PartyIndex, type Tactic, type TacticSub } from '@divorceos/schema';
import {
  MIN_TAX_BISECTIONS, MIN_TAX_COARSE_STEPS, MIN_TAX_DERIVATIVE_EPS, MIN_TAX_DERIVATIVE_H, TACTIC_DIVERGENCE_LIMIT,
  MAX_CHILDREN, TACTIC_MAX_ITER, TACTIC_TAX_TOLERANCE, TACTIC_TAX_TOLERANCE_SMALL,
} from './constants.js';
import { computeGuideline, receivedBy, signedBy, type GuidelineOutcome } from './guideline.js';
import { ageAtYearEnd, type NCase } from './normalize.js';
import type { ScenarioSpec, TaxContext, TaxOutcome } from './taxes.js';

export interface ProposedOutcome {
  /** p0-signed CS (non-deductible), SS (deductible), add-ons and total. */
  cs: number; ss: number; addOns: number; total: number;
  releases: number;
  converged: boolean;
  /** Combined guideline income taxes − combined proposed income taxes (monthly). */
  saving: number;
  taxes: [TaxOutcome, TaxOutcome];
  scenario: ScenarioSpec;
  description: string;
}

/**
 * Form 8332 releases (FUN_10007981 / FUN_100079e0): the LOWER-net party (out+4) releases all of its dependents to
 * the higher-net party (out+2) — the count is its dependents line, whatever the CTC count — capped by `requested`
 * when the caller gives a number (the Settlement table); the Proposed column of the original has no such input.
 */
export function resolveReleases(nc: NCase, g: GuidelineOutcome, requested: number | null): number {
  if (!nc.c.settings.releaseExemptions8332) return 0;
  const payee = otherParty(g.hi);
  const available = Math.max(0, Math.floor(nc.parties[payee].exemptions.dependent));
  return requested === null ? available : Math.max(0, Math.min(Math.floor(requested), available));
}

/** A per-child release resolved from ProposedSettings.releaseChildren. */
export interface ChildReleases {
  /** Indices into Case.children actually released (valid, unique, custodial parent = the releasing party). */
  children: number[];
  count: number;
  /** Released children under 17 at year end (CTC) / 17 and over (other dependents, ODC). */
  ctc: number;
  odc: number;
}

/**
 * ProposedSettings.releaseChildren (optional): exactly those children are released — indices are clamped to the
 * children (within the engine's child limit) whose custodial parent is the releasing party; each moves per its age
 * at year end (no DOB = under 17, as the exemption tally). Null when the field is absent or releases are blocked.
 */
export function resolveChildReleases(nc: NCase, g: GuidelineOutcome): ChildReleases | null {
  const list = nc.c.proposed.releaseChildren;
  if (!Array.isArray(list)) return null;
  if (!nc.c.settings.releaseExemptions8332) return { children: [], count: 0, ctc: 0, odc: 0 };
  const payee = otherParty(g.hi);
  const kids = nc.c.children.slice(0, MAX_CHILDREN);
  const children = [...new Set(list)].filter((i) => Number.isInteger(i) && i >= 0 && i < kids.length && kids[i].custodialParent === payee).sort((a, b) => a - b);
  let ctc = 0, odc = 0;
  for (const i of children) {
    const age = ageAtYearEnd(kids[i].dateOfBirth, nc.year) ?? 0;
    if (age < 17) ctc += 1; else odc += 1;
  }
  return { children, count: children.length, ctc, odc };
}

/** The party that releases exemptions: the lower-net party (out+4). */
export const releasingParty = (g: GuidelineOutcome): PartyIndex => otherParty(g.hi);

/** Target machinery shared by tactics 2..8 and the Settlement table. */
export class TacticSolver {
  readonly nc: NCase;
  readonly payee: PartyIndex;
  /** Formula nets (incl. CS-only income) — the base of "net spendable". */
  readonly fn: [number, number];
  /** Guideline net spendable per party. */
  readonly nsGuideline: [number, number];
  readonly gTaxes: [TaxOutcome, TaxOutcome];

  constructor(readonly ctx: TaxContext, readonly g: GuidelineOutcome, readonly releases: number, readonly tactic: number,
    readonly releaseKids?: { ctc: number; odc: number }) {
    this.nc = ctx.nc;
    this.payee = releasingParty(g);
    this.fn = [g.nets[0].formulaNet, g.nets[1].formulaNet];
    const sg = this.savings(g.taxes);
    this.nsGuideline = [this.fn[0] + sg[0] + receivedBy(g.total, 0), this.fn[1] + sg[1] + receivedBy(g.total, 1)];
    this.gTaxes = g.taxes;
  }

  spec(ss: number): ScenarioSpec {
    const base: ScenarioSpec = { ss, releases: this.releases, releaseFrom: this.payee, zeroDependents: null };
    return this.releaseKids ? { ...base, releaseKids: this.releaseKids } : base;
  }

  taxes(ss: number): [TaxOutcome, TaxOutcome] { return this.ctx.scenario(this.spec(ss)); }

  /** Net spendable of party p: formula net + (no-support tax − tax) ± support (the Cash Flow definition). */
  savings(t: [TaxOutcome, TaxOutcome]): [number, number] {
    const b = this.g.noSupportTaxes;
    return [b[0].incomeTax - t[0].incomeTax, b[1].incomeTax - t[1].incomeTax];
  }

  /** sav over guideline: guideline tax(p) − tax(p) (the "increase over GDL" of tactics 6/7). */
  savingsOverGuideline(t: [TaxOutcome, TaxOutcome]): [number, number] {
    return [this.gTaxes[0].incomeTax - t[0].incomeTax, this.gTaxes[1].incomeTax - t[1].incomeTax];
  }

  /**
   * p0-signed total support (CS + SS + add-ons) that hits the tactic's target with the given taxes
   * (§4 "Targets"). Net spendable NS(p) = formula net + sav(p) ± support, sav vs the no-support taxes so the
   * target shows up unchanged in the Cash Flow view.
   */
  targetTotal(t: [TaxOutcome, TaxOutcome]): number {
    const [f0, f1] = this.fn;
    const sav = this.savings(t);
    const X = this.nc.c.proposed.targetAmount;
    const P = this.nc.c.proposed.targetPercent / 100;
    const C = f0 + f1 + sav[0] + sav[1];
    const gT = this.g.total;
    switch (this.tactic) {
      case 2: return f0 + sav[0] - X;
      case 3: return X - f1 - sav[1];
      case 4: return f0 + sav[0] - P * C;
      case 5: return P * C - f1 - sav[1];
      case 6: case 7: {
        // the combined tax saving over the guideline is split: P to party 0 (t=6) or to party 1 (t=7)
        const sg = this.savingsOverGuideline(t);
        const share = this.tactic === 6 ? P : 1 - P;
        return gT + sg[0] - share * (sg[0] + sg[1]);
      }
      default: { // 8: split combined net spendable in the guideline ratio
        const [w0, w1] = this.nsGuideline;
        if (w0 + w1 <= 0) return f0 + sav[0];
        return f0 + sav[0] - C * w0 / (w0 + w1);
      }
    }
  }

  /** FUN_10007619: CS that meets the target for a given SS (one pass — CS is not taxable). The proposed column has no add-on line: the target is met by CS + SS alone. */
  solveCs(ss: number): { cs: number; taxes: [TaxOutcome, TaxOutcome] } {
    const t = this.taxes(ss);
    const T = this.targetTotal(t);
    return { cs: T - ss, taxes: t };
  }

  /** FUN_100071b0: SS that meets the target for a given CS, iterating the tax consequences. */
  solveSs(cs: number, start = 0): { ss: number; taxes: [TaxOutcome, TaxOutcome]; converged: boolean } {
    let ss = start;
    let t = this.taxes(ss);
    let converged = false;
    for (let i = 0; i < TACTIC_MAX_ITER; i++) {
      const T = this.targetTotal(t);
      const next = T - cs;
      if (Math.abs(next) > TACTIC_DIVERGENCE_LIMIT) { ss = next; break; }
      const tn = this.taxes(next);
      const ok = ([0, 1] as const).every((p) => {
        const tol = Math.abs(tn[p].incomeTax) < 10 ? TACTIC_TAX_TOLERANCE_SMALL : TACTIC_TAX_TOLERANCE;
        return Math.abs(tn[p].incomeTax - t[p].incomeTax) < tol;
      });
      ss = next; t = tn;
      if (ok) { converged = true; break; }
    }
    return { ss, taxes: t, converged };
  }

  /** Combined monthly income tax of both parties at a p0-signed SS. */
  combinedTax(ss: number): number {
    const t = this.taxes(ss);
    return t[0].incomeTax + t[1].incomeTax;
  }

  /**
   * FUN_100089cb: tax-minimising SS (relative to the higher-net party out+2), replicated step by step:
   *  - interval [−AGI(payee), AGI(payor)] (no-support federal AGIs / 12), step = |range| / 50;
   *  - a coarse walk (FUN_10008884) from the MIDPOINT of the interval in the direction the numerical derivative
   *    (FUN_1000879d, h = 0.1, "non-decreasing" when > −1e-7) says the tax falls, until a step brackets a minimum
   *    (decreasing at x, non-decreasing at x + step) — then 6 bisections (FUN_1000896b) — or a bound is hit
   *    (the clamped point is the candidate and the search ends there);
   *  - a second walk from the guideline SS on the side of the first candidate; the lower-tax candidate wins;
   *  - the guideline SS itself when its combined tax is lower than the winner's. Guideline SS for MFJIN.
   */
  minimiseTaxSs(): number {
    const g = this.g;
    if (this.nc.parties[0].status === 'MFJIN' || this.nc.parties[1].status === 'MFJIN') return g.ss;
    const payor = g.hi;
    const payee = otherParty(payor);
    const rel = (p0: number) => (payor === 0 ? p0 : -p0);
    const base = g.noSupportTaxes;
    const hi0 = base[payor].federal.agi / 12;
    const lo0 = -base[payee].federal.agi / 12;
    const step = Math.abs((hi0 - lo0) / MIN_TAX_COARSE_STEPS);
    const f = (x: number) => this.combinedTax(signedBy(x, payor));
    /** FUN_1000885a: true when the tax is not decreasing at x. */
    const nonDecreasing = (x: number) => (f(x + MIN_TAX_DERIVATIVE_H) - f(x)) / MIN_TAX_DERIVATIVE_H > MIN_TAX_DERIVATIVE_EPS;
    const gSs = rel(g.ss);
    /** FUN_10008884 + FUN_1000896b from `start` on [lo, hi]: the candidate SS. */
    const search = (start: number, lo: number, hi: number): number => {
      let x = start, x2 = start + step;
      let fx = nonDecreasing(x), fx2 = nonDecreasing(x2);
      const flag0 = fx;
      for (let count = 1; ; count++) {
        if (x < lo) return lo;                 // hit the lower bound
        if (x2 > hi) return hi - step;         // hit the upper bound
        if ((!fx && fx2) || count > MIN_TAX_COARSE_STEPS) break;
        if (!fx) { x = x2; fx = fx2; x2 += step; fx2 = nonDecreasing(x2); } // falling at x: move right
        else { x2 = x; fx2 = fx; x -= step; fx = nonDecreasing(x); }        // move left
      }
      // 6 bisections; the side that moves is chosen by comparing the midpoint's flag with the start's (as the original)
      let a = x, b = x2;
      let m = (a + b) / 2;
      for (let i = 0; i < MIN_TAX_BISECTIONS; i++) {
        if (nonDecreasing(m) === flag0) a = m; else b = m;
        m = (a + b) / 2;
      }
      return m;
    };
    if (step <= 0) return g.ss;
    const cand1 = search((hi0 + lo0) / 2, lo0, hi0);
    const cand2 = cand1 <= gSs ? search(gSs, cand1, hi0) : search(gSs - step, lo0, cand1);
    let best = cand1, bestTax = f(cand1);
    const tax2 = f(cand2);
    if (tax2 < bestTax) { best = cand2; bestTax = tax2; }
    if (f(gSs) < bestTax) return g.ss;
    return signedBy(best, payor);
  }

  /** §4 sub-options 0..5 for tactics 2..8 (and a..f on tactic 8's target). */
  solve(sub: TacticSub): { cs: number; ss: number; converged: boolean } {
    const pr = this.nc.c.proposed;
    const csU = signedBy(pr.userCs, pr.userCsPayor);
    const ssU = signedBy(pr.userSs, pr.userSsPayor);
    switch (sub) {
      case 0: {
        const ss = this.minimiseTaxSs();
        const { cs } = this.solveCs(ss);
        if (cs * ss < 0) {
          // two-way payment not allowed: all as SS unless that costs more tax than all as CS
          const r = this.solveSs(0, ss);
          const withSs = this.combinedTax(r.ss);
          const withoutSs = this.combinedTax(0);
          if (withSs <= withoutSs) return { cs: 0, ss: r.ss, converged: r.converged };
          return { cs: this.solveCs(0).cs, ss: 0, converged: true };
        }
        return { cs, ss, converged: true };
      }
      case 1: {
        const ss = this.minimiseTaxSs();
        return { cs: this.solveCs(ss).cs, ss, converged: true };
      }
      case 2: {
        const lim = Math.abs(ssU);
        const ss = Math.max(-lim, Math.min(lim, this.minimiseTaxSs()));
        return { cs: this.solveCs(ss).cs, ss, converged: true };
      }
      case 3: return { cs: this.solveCs(ssU).cs, ss: ssU, converged: true };
      case 4: { const r = this.solveSs(csU); return { cs: csU, ss: r.ss, converged: r.converged }; }
      default: { const r = this.solveSs(0); return { cs: 0, ss: r.ss, converged: r.converged }; }
    }
  }
}

/** Numeric tactic (2..8) and sub-option for a tactic key; letters a..f = sub 0..5 on tactic 8's target. */
export function tacticTarget(t: Tactic, sub: TacticSub): { tactic: number; sub: TacticSub } {
  const letters = 'abcdef';
  if (letters.includes(t)) return { tactic: 8, sub: letters.indexOf(t) as TacticSub };
  return { tactic: Number(t), sub };
}

export function computeProposed(ctx: TaxContext, g: GuidelineOutcome, now: Date): ProposedOutcome {
  const nc = ctx.nc;
  const pr = nc.c.proposed;
  // The original's Proposed column always releases the maximum (its per-case count only feeds the Settlement table).
  // ProposedSettings.releaseChildren (optional) replaces that with exactly the listed children, each per its age.
  const childReleases = resolveChildReleases(nc, g);
  const releases = childReleases ? childReleases.count : resolveReleases(nc, g, null);
  const releaseKids = childReleases ? { ctc: childReleases.ctc, odc: childReleases.odc } : undefined;
  const payee = releasingParty(g);
  const spec = (ss: number): ScenarioSpec => (releaseKids
    ? { ss, releases, releaseFrom: payee, zeroDependents: null, releaseKids }
    : { ss, releases, releaseFrom: payee, zeroDependents: null });
  // The original's Proposed column (out+0x8c / out+0x90) has only a non-deductible and a deductible amount: add-ons
  // are folded into the CS by tactic 9 and are simply not part of the other tactics' amounts.
  let cs = 0, ss = 0, converged = true;
  const addOns = 0;
  const { tactic, sub } = tacticTarget(pr.tactic, pr.sub);
  if (tactic === 0) {
    // "Make Guideline total fully deductible" (FUN_10008ca4 t=0): CS = 0, SS = guideline CS + guideline SS.
    ss = g.presumedCs + g.ss; cs = 0;
  } else if (tactic === 1) {
    cs = signedBy(pr.userCs, pr.userCsPayor); ss = signedBy(pr.userSs, pr.userSsPayor);
  } else if (tactic === 9) {
    // Guideline rerun in tactic mode with the released exemptions (FUN_100152cc): CS = result CS + add-ons, SS = result SS.
    const g9 = computeGuideline(ctx, { releases, releaseFrom: payee, ...(releaseKids ? { releaseKids } : {}), now, tacticMode: true, fixedHardship: g.hardshipCalc });
    cs = g9.presumedCs + g9.addOns; ss = g9.ss;
  } else {
    const solver = new TacticSolver(ctx, g, releases, tactic, releaseKids);
    const r = solver.solve(sub);
    cs = r.cs; ss = r.ss; converged = r.converged;
    if (!Number.isFinite(cs) || !Number.isFinite(ss) || Math.abs(cs) > TACTIC_DIVERGENCE_LIMIT || Math.abs(ss) > TACTIC_DIVERGENCE_LIMIT) {
      converged = false; cs = g.presumedCs; ss = g.ss;
    }
  }
  const sc = spec(ss);
  const taxes = ctx.scenario(sc);
  const saving = g.taxes[0].incomeTax + g.taxes[1].incomeTax - taxes[0].incomeTax - taxes[1].incomeTax;
  return { cs, ss, addOns, total: cs + ss + addOns, releases, converged, saving, taxes, scenario: sc, description: TACTIC_DESCRIPTION[pr.tactic] };
}

